import { camelCase, mapKeys } from 'lodash-es';

import { dark } from './color.palette';

// both `fill` and `stroke` share the same colors, for now
const svgColors = {
  default: dark.shark11,
  emphasis: dark.shark12,
  secondary: dark.shark10,
  tertiary: dark.shark9,
  disabled: dark.shark8,
  placeholder: dark.shark7,
  inverse: dark.shark1,
  brand: dark.cantaloupe7,
  brandOnFill: dark.shark1,
  info: dark.blue9,
  infoOnFill: dark.blue10,
  positive: dark.green9,
  positiveOnFill: dark.green10,
  caution: dark.amber9,
  cautionOnFill: dark.amber10,
  critical: dark.red9,
  criticalOnFill: dark.red10,
  highlight: dark.violet9,
  highlightOnFill: dark.violet10,

  bull: '#70FF93',
  bullSecondary: '#00B277',
  bear: '#FE4363',
  bearSecondary: '#DB2951',
  patronage: '#FFA548',
};

const textColors = {
  default: dark.shark11,
  emphasis: dark.shark12,
  secondary: dark.shark10,
  tertiary: dark.shark9,
  disabled: dark.shark8,
  link: dark.cantaloupe7,
  linkHover: dark.cantaloupe8,
  linkPress: dark.cantaloupe7,
  brand: dark.cantaloupe7,
  brandOnFill: dark.shark1,
  info: dark.blue10,
  infoOnFill: dark.blue11,
  positive: dark.green10,
  positiveOnFill: dark.green11,
  caution: dark.amber10,
  cautionOnFill: dark.amber11,
  critical: dark.red10,
  criticalOnFill: dark.red11,
  highlight: dark.violet10,
  highlightOnFill: dark.violet11,
  inverse: dark.shark1,
  inverseLink: dark.shark1,

  bull: '#70FF93',
  bullSecondary: '#00B277',
  bear: '#FE4363',
  bearSecondary: '#DB2951',

  patronage: '#FFA548',
};

export const decorativeColors = {
  red: '#E5484D',
  orange: '#FE6F39',
  yellow: '#FFC53D',
  green: '#3DD68C',
  cyan: '#23AFD0',
  indigo: '#5472E4',
  purple: '#9A5CD0',
  pink: '#D6409F',
} as const;

export type DecorativeColorKey = keyof typeof decorativeColors;

export const colors = {
  static: {
    black: '#000000',
    white: '#FFFFFF',

    ...mapKeys(decorativeColors, (_, key) => camelCase(`decorative ${key}`)),
  },
  background: {
    canvas: dark.shark1,
    canvasInverse: dark.shark12,

    surface: dark.shark2,
    surfaceInverse: dark.shark11,
    surfaceHover: dark.shark3,
    surfacePress: dark.shark2,
    surfaceSelected: dark.shark3,
    surfaceDisabled: dark.shark2,

    surfaceTwo: dark.shark3,
    surfaceTwoHover: dark.shark4,
    surfaceTwoPress: dark.shark3,
    surfaceTwoSelected: dark.shark4,
    surfaceTwoDisabled: dark.shark2,

    surfaceAlt: dark.shark1,
    surfaceAltHover: dark.shark2,
    surfaceAltPress: dark.shark1,
    surfaceAltSelected: dark.shark2,
    surfaceAltDisabled: dark.shark1,

    surfaceBrand: dark.cantaloupe2,
    surfaceBrandHover: dark.cantaloupe3,
    surfaceBrandPress: dark.cantaloupe2,
    surfaceInfo: dark.blue2,
    surfaceInfoHover: dark.blue3,
    surfaceInfoPress: dark.blue2,
    surfacePositive: dark.green2,
    surfacePositiveHover: dark.green3,
    surfacePositivePress: dark.green2,
    surfaceCaution: dark.amber2,
    surfaceCautionHover: dark.amber3,
    surfaceCautionPress: dark.amber2,
    surfaceCritical: dark.red2,
    surfaceCriticalHover: dark.red3,
    surfaceCriticalPress: dark.red2,
    surfaceHighlight: dark.violet2,
    surfaceHighlightHover: dark.violet3,
    surfaceHighlightPress: dark.violet2,

    fill: dark.shark4,
    fillHover: dark.shark5,
    fillPress: dark.shark4,
    fillSelected: dark.shark5,
    fillDisabled: dark.sharkAlpha3,

    fillTwo: dark.shark5,
    fillTwoHover: dark.shark6,
    fillTwoPress: dark.shark5,
    fillTwoSelected: dark.shark6,
    fillTwoDisabled: dark.sharkAlpha4,

    fillAlt: dark.shark1,
    fillAltHover: dark.shark2,
    fillAltPress: dark.shark1,
    fillAltSelected: dark.sharkAlpha2,
    fillAltDisabled: dark.shark2,

    fillBrand: dark.cantaloupe7,
    fillBrandEmphasis: dark.cantaloupe6,
    fillBrandHover: dark.cantaloupe8,
    fillBrandPress: dark.cantaloupe6,
    fillBrandSelected: dark.cantaloupe7,
    fillBrandDisabled: dark.cantaloupe7,
    fillInfo: dark.blue3,
    fillInfoHover: dark.blue4,
    fillInfoPress: dark.blue3,
    fillInfoEmphasis: dark.blue9,
    fillPositive: dark.green3,
    fillPositiveHover: dark.green4,
    fillPositivePress: dark.green3,
    fillPositiveEmphasis: dark.green9,
    fillCaution: dark.amber3,
    fillCautionHover: dark.amber4,
    fillCautionPress: dark.amber3,
    fillCautionEmphasis: dark.amber9,
    fillCritical: dark.red3,
    fillCriticalHover: dark.red4,
    fillCriticalPress: dark.red3,
    fillCriticalEmphasis: dark.red9,
    fillHighlight: dark.violet3,
    fillHighlightHover: dark.violet4,
    fillHighlightPress: dark.violet3,
    fillHighlightEmphasis: dark.violet9,

    overlay: 'rgb(0 0 0 / 0.8)',
    placeholder: dark.sharkAlpha4,

    fillBullEmphasis: '#70FF93',
    fillBearEmphasis: '#FE4363',
  },
  border: {
    default: dark.sharkAlpha4,
    inverse: dark.shark12,
    emphasis: dark.sharkAlpha5,
    secondary: dark.sharkAlpha3,
    tertiary: dark.sharkAlpha2,
    disabled: dark.sharkAlpha3,
    selected: dark.cantaloupe7,
    focusRing: dark.shark11,
    brand: dark.cantaloupe7,
    info: dark.blue4,
    infoEmphasis: dark.blue8,
    positive: dark.green4,
    positiveEmphasis: dark.green8,
    caution: dark.amber4,
    cautionEmphasis: dark.amber8,
    critical: dark.red4,
    criticalEmphasis: dark.red8,
    highlight: dark.violet4,
    highlightEmphasis: dark.violet8,
  },
  divider: {
    default: dark.sharkAlpha4,
    emphasis: dark.sharkAlpha5,
    secondary: dark.sharkAlpha3,
    tertiary: dark.sharkAlpha2,
    inverse: dark.shark12,
  },
  outline: {
    critical: dark.red8,
    selected: dark.cantaloupe7,
    focusRing: dark.shark11,
  },
  ring: {
    critical: dark.red8,
    selected: dark.cantaloupe7,
    focusRing: dark.shark11,
  },

  fill: svgColors,
  stroke: svgColors,

  text: textColors,
  textDecoration: textColors,
} as const;
